/// Port of mobile/lib/game/enemy_manager.dart.

import { Component } from './engine/component';
import type { Rng } from './engine/random';
import { defaultRng } from './engine/random';
import { Enemy } from './enemy';
import type { CactusType, EnemyData } from './enemyData';
import { getEnemyDataList } from './enemyData';
import { obstacleGap } from './gamePhysics';
import { groundLevelY, worldWidth } from './world';

export const maxObstacleDuplication = 2;
const maxSpawnAttempts = 16;

/// Y positions for pterodactyls (bottom-left anchor, world coordinates).
/// Tuned against dino running height (47px) and ducking height (30px)
/// with the ground line at 140.
export const pteroYPositions: readonly number[] = [
  122.0, // low - must jump
  107.0, // mid - can duck or jump
  88.0, // high - passes overhead
];

interface EnemyManagerDeps {
  isPlaying: () => boolean;
  currentSpeed: () => number;
  baseSpeed: () => number;
  isDarkMode: () => boolean;
  /** Where spawned enemies are mounted: the game world, not this manager. */
  world: Component;
  rng?: Rng;
}

export class EnemyManager extends Component {
  private currentGapDistance = 0;
  private enemyDataList: EnemyData[] = [];
  private obstacleHistory: CactusType[] = [];

  /** Enemies currently on the world. Avoids scanning children every step. */
  readonly liveEnemies: Enemy[] = [];

  private readonly rng: Rng;
  private readonly deps: EnemyManagerDeps;

  constructor(deps: EnemyManagerDeps) {
    super(2);
    this.deps = deps;
    this.rng = deps.rng ?? defaultRng;
  }

  override onMount(): void {
    if (this.enemyDataList.length === 0) {
      this.enemyDataList = getEnemyDataList(this.deps.isDarkMode());
    }
  }

  removeAllEnemies(): void {
    for (const enemy of [...this.liveEnemies]) {
      enemy.removeFromParent();
    }
    this.liveEnemies.length = 0;
  }

  reset(): void {
    this.removeAllEnemies();
    this.obstacleHistory = [];
    this.currentGapDistance = 0;
  }

  reloadThemeAssets(): void {
    this.enemyDataList = getEnemyDataList(this.deps.isDarkMode());
    if (!this.deps.isPlaying()) {
      this.removeAllEnemies();
    }
  }

  private setGapFrom(data: EnemyData): void {
    this.currentGapDistance = obstacleGap({
      width: data.textureSize.x,
      baseSpeed: this.deps.baseSpeed(),
      typeMinGap: data.typeMinGap,
      nextInt: (max) => this.rng.nextInt(max),
    });
  }

  /// Fixed-timestep spawn countdown.
  stepPhysics(dt: number): void {
    if (!this.deps.isPlaying()) return;

    this.currentGapDistance -= this.deps.currentSpeed() * dt;

    if (this.currentGapDistance <= 0) {
      this.spawnRandomEnemy();
    }
  }

  private isDuplicateObstacle(type: CactusType): boolean {
    let count = 0;
    for (const previous of this.obstacleHistory) {
      if (previous === type) count += 1;
    }
    return count >= maxObstacleDuplication;
  }

  private spawnRandomEnemy(attempt = 0): void {
    if (attempt >= maxSpawnAttempts) return;

    if (this.enemyDataList.length === 0) {
      this.enemyDataList = getEnemyDataList(this.deps.isDarkMode());
      return;
    }

    const randomIndex = this.rng.nextInt(this.enemyDataList.length);
    const data = this.enemyDataList[randomIndex];

    if (this.deps.baseSpeed() < data.minSpeed) {
      this.spawnRandomEnemy(attempt + 1);
      return;
    }

    if (data.cactusType !== null) {
      if (this.isDuplicateObstacle(data.cactusType)) {
        this.spawnRandomEnemy(attempt + 1);
        return;
      }

      this.obstacleHistory.unshift(data.cactusType);
      if (this.obstacleHistory.length > maxObstacleDuplication) {
        this.obstacleHistory.pop();
      }
    }

    const yPosition = data.canFly
      ? pteroYPositions[this.rng.nextInt(pteroYPositions.length)]
      : groundLevelY;

    const enemy = new Enemy(data, this.deps.isPlaying, this.deps.currentSpeed);
    enemy.position = { x: worldWidth + enemy.size.x, y: yPosition };

    this.liveEnemies.push(enemy);
    this.deps.world.add(enemy);
    this.setGapFrom(data);
  }

  pruneRemoved(): void {
    for (let i = this.liveEnemies.length - 1; i >= 0; i -= 1) {
      const enemy = this.liveEnemies[i]!;
      if (enemy.parent === null || enemy.position.x < -enemy.size.x) {
        this.liveEnemies.splice(i, 1);
      }
    }
  }
}
